import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
// Domain
import { useMemo } from 'react';
import * as LocalesUseCases from './localeUseCases';
// Services
import { useGetAllLocalesQuery } from '../../../services/cortizol/locale/localeQueries';

/**
 *
 * @returns {{ createLocalesCollection: LocalesUseCases.CreateLocalesCollection,
 * currentLocale: string | undefined,
 * defaultLocale: string | undefined,
 * localesOptions: Array<{ label: string, value: string }> }}
 */
const useLocale = () => {
  // Hooks
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  // Calls
  const getAllLocalesQuery = useGetAllLocalesQuery({
    select: (data) => ({
      data: { ...LocalesUseCases.createLocalesCollection(data.data, t) },
      default: data.default,
    }),
  });

  // Methods
  /**
   * The current locale based on the search parameters or the default locale from the API.
   * @type {string}
   */
  const currentLocale = useMemo(() => {
    const localeFromParams = searchParams.get('locale');
    const defaultFromAPI = getAllLocalesQuery?.data?.default;

    return localeFromParams || defaultFromAPI;
  }, [searchParams, getAllLocalesQuery.data]);

  /**
   * Retrieves the options for locales.
   * @returns {Array<{label: string, value: string}>} The array of locale options.
   */
  const localesOptions = useMemo(() => (getAllLocalesQuery.data?.data
    ? Object.keys(getAllLocalesQuery.data.data).map((key) => ({
      value: getAllLocalesQuery.data.data[key].value,
      label: getAllLocalesQuery.data.data[key].label,
    }))
    : []), [getAllLocalesQuery.data]);

  return {
    currentLocale,
    defaultLocale: getAllLocalesQuery.data?.default,
    localesOptions,
    createLocalesCollection: LocalesUseCases.createLocalesCollection,
  };
};

export default useLocale;
